import React, { Component } from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import moment from 'moment';
import { getUserTimeSummary } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';

/**
 * MyTime - Technician time log view (read-only)
 *
 * Mobile-first view showing a technician's own time sessions
 * with summary stats and card-based session list.
 *
 * Props:
 * - user: Current user object
 * - typeId: Company/installer ID
 * - selectedLocationId: Current location filter
 */
class MyTime extends Component {
  state = {
    sessions: [],
    summary: null,
    loading: true,
    error: null,
    range: 'week',
    selectedDate: moment().startOf('isoWeek')
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.range !== this.state.range ||
      !prevState.selectedDate.isSame(this.state.selectedDate, 'day')
    ) {
      this.loadData();
    }
  }

  getDateRange = () => {
    const { range, selectedDate } = this.state;
    let start, end;

    if (range === 'day') {
      start = moment(selectedDate).startOf('day');
      end = moment(selectedDate).endOf('day');
    } else if (range === 'week') {
      start = moment(selectedDate).startOf('isoWeek');
      end = moment(selectedDate).endOf('isoWeek');
    } else {
      start = moment(selectedDate).startOf('month');
      end = moment(selectedDate).endOf('month');
    }

    return { start, end };
  };

  loadData = async () => {
    const { user } = this.props;
    if (!user?.User_id) return;

    this.setState({ loading: true, error: null });

    try {
      const { start, end } = this.getDateRange();
      const result = await getUserTimeSummary(
        user.User_id,
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      );

      this.setState({
        sessions: result.sessions || [],
        summary: result.summary || { total_hours: 0, session_count: 0, days_worked: 0 },
        loading: false
      });
    } catch (error) {
      console.error('Error loading time data:', error);
      this.setState({
        error: 'Failed to load time data',
        loading: false
      });
    }
  };

  // Range toggle handler
  handleRange = (newRange) => {
    let newDate = moment(this.state.selectedDate);

    if (newRange === 'day') {
      newDate = moment();
    } else if (newRange === 'week') {
      newDate = moment().startOf('isoWeek');
    } else {
      newDate = moment().startOf('month');
    }

    this.setState({ range: newRange, selectedDate: newDate });
  };

  // Period navigation
  handleNav = (direction) => {
    const { range, selectedDate } = this.state;
    let newDate = moment(selectedDate);

    if (direction === 'today') {
      if (range === 'day') newDate = moment();
      else if (range === 'week') newDate = moment().startOf('isoWeek');
      else newDate = moment().startOf('month');
    } else if (direction === 'prev') {
      if (range === 'day') newDate.subtract(1, 'day');
      else if (range === 'week') newDate.subtract(1, 'week');
      else newDate.subtract(1, 'month');
    } else {
      if (range === 'day') newDate.add(1, 'day');
      else if (range === 'week') newDate.add(1, 'week');
      else newDate.add(1, 'month');
    }

    this.setState({ selectedDate: newDate });
  };

  getDisplayString = () => {
    const { range, selectedDate } = this.state;

    if (range === 'day') {
      return selectedDate.format('ddd, MMM D, YYYY');
    } else if (range === 'week') {
      const start = moment(selectedDate).startOf('isoWeek');
      const end = moment(selectedDate).endOf('isoWeek');
      return start.format('MMM D') + ' \u2013 ' + end.format('MMM D, YYYY');
    } else {
      return selectedDate.format('MMMM YYYY');
    }
  };

  formatDuration = (totalSeconds, checkInAt, checkOutAt) => {
    if ((totalSeconds === null || totalSeconds === undefined) && checkInAt && checkOutAt) {
      totalSeconds = convertFromUTC(checkOutAt).diff(convertFromUTC(checkInAt), 'seconds');
    }

    if (!totalSeconds && totalSeconds !== 0) return null;

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  getBorderColor = (status) => {
    switch (status) {
      case 'flagged': return '#ffc107';
      case 'auto_closed': return '#dc3545';
      case 'open': return '#007bff';
      default: return '#28a745';
    }
  };

  renderSummary = () => {
    const { summary } = this.state;
    if (!summary) return null;

    const hours = summary.total_hours != null
      ? (Math.round(summary.total_hours * 10) / 10)
      : 0;

    return (
      <div className="mytime-summary">
        <div className="mytime-summary-item primary">
          <span className="mytime-summary-value">{hours}</span>
          <span className="mytime-summary-label">hrs</span>
        </div>
        <div className="mytime-summary-divider" />
        <div className="mytime-summary-item">
          <span className="mytime-summary-value">{summary.days_worked || 0}</span>
          <span className="mytime-summary-label">{summary.days_worked === 1 ? 'day' : 'days'}</span>
        </div>
      </div>
    );
  };

  renderSessionCard = (session) => {
    const borderColor = this.getBorderColor(session.status);
    const checkIn = convertFromUTC(session.check_in_at);
    const checkOut = session.check_out_at ? convertFromUTC(session.check_out_at) : null;
    const isOpen = session.status === 'open';
    const isFlagged = session.status === 'flagged';
    const isAutoClosed = session.status === 'auto_closed';

    const duration = isOpen
      ? null
      : this.formatDuration(session.total_seconds, session.check_in_at, session.check_out_at);

    const dateStr = checkIn ? checkIn.format('ddd, MMM D') : '';
    const inTime = checkIn ? checkIn.format('h:mm A') : '';
    const outTime = checkOut ? checkOut.format('h:mm A') : '';
    const timeRange = isOpen
      ? `${inTime} \u2013 ...`
      : `${inTime} \u2013 ${outTime}`;

    return (
      <div
        key={session.id}
        className="mytime-card"
        style={{ borderLeftColor: borderColor }}
      >
        <div className="mytime-card-top">
          <div className="mytime-card-date">
            {dateStr}
            {(isFlagged || isAutoClosed) && (
              <i
                className="fa-sharp fa-solid fa-triangle-exclamation mytime-card-warning"
                title={isFlagged ? 'Flagged' : 'Auto-closed'}
              />
            )}
          </div>
          <div className={`mytime-card-duration ${isOpen ? 'in-progress' : ''}`}>
            {isOpen ? 'In progress' : duration}
          </div>
        </div>
        <div className="mytime-card-times">{timeRange}</div>
      </div>
    );
  };

  render() {
    const { sessions, loading, error, range } = this.state;

    return (
      <div className="mytime-container">
        {/* Range Toggle */}
        <div className="mytime-range-toggle">
          {['day', 'week', 'month'].map(r => (
            <button
              key={r}
              className={range === r ? 'active' : ''}
              onClick={() => this.handleRange(r)}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>

        {/* Period Navigation */}
        <div className="mytime-period-nav">
          <ButtonGroup>
            <Button variant="outline-secondary" size="sm" onClick={() => this.handleNav('prev')}>
              <i className="fa-sharp fa-regular fa-chevron-left" />
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => this.handleNav('today')}>
              Today
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => this.handleNav('next')}>
              <i className="fa-sharp fa-regular fa-chevron-right" />
            </Button>
          </ButtonGroup>
          <div className="mytime-period-label">{this.getDisplayString()}</div>
        </div>

        {/* Summary Banner */}
        {!loading && !error && this.renderSummary()}

        {/* Content */}
        {loading ? (
          <div className="mytime-loading">
            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x" />
          </div>
        ) : error ? (
          <div className="mytime-error">
            <i className="fa-sharp fa-regular fa-circle-exclamation" />
            <span>{error}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="mytime-empty">
            <i className="fa-sharp fa-regular fa-clock fa-3x" />
            <p>No sessions for this period</p>
          </div>
        ) : (
          <div className="mytime-sessions">
            {sessions.map(s => this.renderSessionCard(s))}
          </div>
        )}

        <style>{`
          .mytime-container {
            padding: 16px;
            max-width: 600px;
            margin: 0 auto;
            background: #f3f4f6;
            min-height: 80vh;
          }

          /* Range Toggle */
          .mytime-range-toggle {
            display: flex;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 12px;
          }

          .mytime-range-toggle button {
            flex: 1;
            background: white;
            border: none;
            padding: 10px 0;
            font-size: 14px;
            font-weight: 500;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .mytime-range-toggle button.active {
            background: #3FB2BE;
            color: white;
          }

          /* Period Nav */
          .mytime-period-nav {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;
          }

          .mytime-period-nav .btn {
            min-width: 44px;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .mytime-period-label {
            flex: 1;
            text-align: center;
            font-size: 15px;
            font-weight: 600;
            color: #374151;
          }

          /* Summary Banner */
          .mytime-summary {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 24px;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 16px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          }

          .mytime-summary-item {
            display: flex;
            align-items: baseline;
            gap: 4px;
          }

          .mytime-summary-item.primary .mytime-summary-value {
            font-size: 28px;
            font-weight: 700;
            color: #374151;
          }

          .mytime-summary-item .mytime-summary-value {
            font-size: 22px;
            font-weight: 600;
            color: #6b7280;
          }

          .mytime-summary-label {
            font-size: 14px;
            color: #9ca3af;
            font-weight: 500;
          }

          .mytime-summary-divider {
            width: 1px;
            height: 32px;
            background: #e5e7eb;
          }

          /* Session Cards */
          .mytime-sessions {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .mytime-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-left: 4px solid #28a745;
            border-radius: 8px;
            padding: 12px 14px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          }

          .mytime-card-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 4px;
          }

          .mytime-card-date {
            font-size: 15px;
            font-weight: 600;
            color: #374151;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .mytime-card-warning {
            color: #f59e0b;
            font-size: 13px;
          }

          .mytime-card-duration {
            font-size: 15px;
            font-weight: 600;
            color: #374151;
          }

          .mytime-card-duration.in-progress {
            color: #007bff;
            font-weight: 500;
            font-size: 13px;
          }

          .mytime-card-times {
            font-size: 13px;
            color: #6b7280;
          }

          /* Loading / Error / Empty */
          .mytime-loading {
            display: flex;
            justify-content: center;
            padding: 48px 0;
            color: #9ca3af;
          }

          .mytime-error {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 32px;
            color: #dc3545;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
          }

          .mytime-empty {
            text-align: center;
            padding: 48px 16px;
            color: #9ca3af;
          }

          .mytime-empty i {
            margin-bottom: 12px;
          }

          .mytime-empty p {
            font-size: 15px;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }
}

export default MyTime;
